import React, { useState, useEffect } from 'react';
import { Phone, CreditCard, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Operator icons/colors
const OPERATOR_STYLES = {
  AM: { name: 'Airtel Money', color: '#E40000', bgColor: '#FEE2E2' },
  OM: { name: 'Orange Money', color: '#FF6600', bgColor: '#FFF7ED' },
  MP: { name: 'M-Pesa', color: '#00A859', bgColor: '#ECFDF5' },
  AF: { name: 'Afrimoney', color: '#1E40AF', bgColor: '#EFF6FF' }
};

function SerdiPayPayment({ appointment, service, onPaymentComplete, onCancel }) {
  const [step, setStep] = useState('form'); // form, processing, manual, success, error
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  const [manualInfo, setManualInfo] = useState(null);

  // Calculate price
  const cdfRate = 2200;
  const priceUSD = Number(service?.price_usd ?? service?.price ?? 25);
  const priceCDF = Number(service?.price_cdf ?? priceUSD * cdfRate);
  const displayPrice = currency === 'USD' ? priceUSD : priceCDF;

  // Fetch operators on mount
  useEffect(() => {
    fetchOperators();
  }, []);

  // Poll for payment status when processing
  useEffect(() => {
    let interval;
    if (step === 'processing' && transactionId) {
      interval = setInterval(() => {
        checkPaymentStatus();
        setPollingCount(c => c + 1);
      }, 5000); // Check every 5 seconds
    }
    return () => clearInterval(interval);
  }, [step, transactionId]);

  const fetchOperators = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/operators`);
      const data = await response.json();
      setOperators(data.operators || []);
    } catch (err) {
      console.error('Failed to fetch operators:', err);
    }
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 9;
  };

  const detectOperator = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    let prefix = cleaned;
    if (cleaned.startsWith('243')) {
      prefix = cleaned.substring(3);
    } else if (cleaned.startsWith('0')) {
      prefix = cleaned.substring(1);
    }
    
    const twoDigits = prefix.substring(0, 2);
    
    for (const op of operators) {
      if (op.prefixes.includes(twoDigits)) {
        return op.code;
      }
    }
    return null;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
    
    // Auto-detect operator
    const detected = detectOperator(value);
    if (detected) {
      setSelectedOperator(detected);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validatePhone(phoneNumber)) {
      setError('Veuillez entrer un numéro de téléphone valide');
      return;
    }
    
    if (!selectedOperator) {
      setError('Veuillez sélectionner un opérateur mobile money');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/payments/serdipay/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          appointment_id: appointment.appointment_id,
          phone_number: phoneNumber,
          operator: selectedOperator,
          currency: currency
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setTransactionId(data.transaction_id);
        // If SerdiPay is down, backend returns manual_mode / pending_manual — switch to manual step
        if (data.manual_mode || data.status === 'pending_manual') {
          setManualInfo({
            amount: data.amount,
            currency: data.currency,
            operator: data.operator,
            reference: data.transaction_id ? String(data.transaction_id).slice(-8) : '',
            instructions: data.instructions || '',
          });
          setStep('manual');
        } else {
          setStep('processing');
        }
      } else {
        setError(data.detail || data.message || 'Erreur lors de l\'initiation du paiement');
      }
    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    if (!transactionId) return;
    
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/payments/serdipay/status/${transactionId}`, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      const data = await response.json();
      
      if (data.status === 'completed') {
        setStep('success');
        setTimeout(() => {
          onPaymentComplete?.(transactionId);
        }, 2000);
      } else if (data.status === 'failed') {
        setStep('error');
        setError('Le paiement a échoué. Veuillez réessayer.');
      } else if (data.status === 'pending_manual') {
        // Backend fell back to manual mode after the polling started — surface it
        setManualInfo({
          amount: data.amount,
          currency: data.currency,
          operator: data.operator,
          reference: transactionId ? String(transactionId).slice(-8) : '',
          instructions: '',
        });
        setStep('manual');
      }
      // If still pending, continue polling
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };


  // Render form step
  if (step === 'form') {
    return (
      <div className="serdipay-payment" data-testid="serdipay-payment-form">
        <div className="payment-header">
          <CreditCard size={32} />
          <h2>Paiement Mobile Money</h2>
          <p>Payez votre rendez-vous en toute sécurité</p>
        </div>

        <div className="payment-summary">
          <div className="summary-row">
            <span>Service:</span>
            <strong>{service?.name || 'Consultation'}</strong>
          </div>
          <div className="summary-row">
            <span>Patient:</span>
            <strong>{appointment?.patient_name}</strong>
          </div>
          <div className="summary-row total">
            <span>Montant à payer:</span>
            <span style={{ textAlign: 'right' }}>
              <strong style={{ fontSize: '1.35rem', color: '#1565C0' }} data-testid="payment-total-display">
                {currency === 'USD'
                  ? `${priceUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`
                  : `${priceCDF.toLocaleString('fr-FR')} CDF`}
              </strong>
              <br />
              <span style={{ fontSize: '0.85rem', color: '#64748b' }} data-testid="payment-total-alt">
                ≈ {currency === 'USD'
                  ? `${priceCDF.toLocaleString('fr-FR')} CDF`
                  : `${priceUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`}
              </span>
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="payment-form">
          {/* Currency Selection — TRÈS visible, en haut */}
          <div className="form-group currency-selector-block" data-testid="currency-selector">
            <label style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0F172A' }}>
              Choisissez votre devise de paiement
            </label>
            <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '4px 0 10px' }}>
              Le paiement Mobile Money accepte le <strong>USD</strong> et le <strong>Franc Congolais (CDF)</strong>.
            </p>
            <div className="currency-toggle currency-toggle-large">
              <button
                type="button"
                className={`currency-btn ${currency === 'USD' ? 'active' : ''}`}
                onClick={() => setCurrency('USD')}
                data-testid="currency-usd-btn"
              >
                <span style={{ fontSize: '1.5rem', display: 'block', lineHeight: 1 }}>$</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>USD</span>
                <span style={{ fontSize: '0.75rem', opacity: 0.8, display: 'block', marginTop: 2 }}>
                  {priceUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </button>
              <button
                type="button"
                className={`currency-btn ${currency === 'CDF' ? 'active' : ''}`}
                onClick={() => setCurrency('CDF')}
                data-testid="currency-cdf-btn"
              >
                <span style={{ fontSize: '1.5rem', display: 'block', lineHeight: 1, fontWeight: 700 }}>FC</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>CDF</span>
                <span style={{ fontSize: '0.75rem', opacity: 0.8, display: 'block', marginTop: 2 }}>
                  {priceCDF.toLocaleString('fr-FR')}
                </span>
              </button>
            </div>
          </div>

          {/* Phone Number */}
          <div className="form-group">
            <label>Numéro de téléphone Mobile Money</label>
            <div className="phone-input">
              <span className="phone-prefix">+243</span>
              <input
                type="tel"
                placeholder="991234567"
                value={phoneNumber}
                onChange={handlePhoneChange}
                maxLength={12}
                data-testid="phone-input"
              />
            </div>
            <small>Entrez le numéro associé à votre compte mobile money</small>
          </div>

          {/* Operator Selection */}
          <div className="form-group">
            <label>Opérateur Mobile Money</label>
            <div className="operator-grid">
              {operators.map(op => (
                <button
                  key={op.code}
                  type="button"
                  className={`operator-btn ${selectedOperator === op.code ? 'selected' : ''}`}
                  style={{
                    '--op-color': OPERATOR_STYLES[op.code]?.color,
                    '--op-bg': OPERATOR_STYLES[op.code]?.bgColor
                  }}
                  onClick={() => setSelectedOperator(op.code)}
                  data-testid={`operator-${op.code}`}
                >
                  <span className="op-icon">
                    {op.code === 'AM' && '📱'}
                    {op.code === 'OM' && '🟠'}
                    {op.code === 'MP' && '💚'}
                    {op.code === 'AF' && '🌍'}
                  </span>
                  <span className="op-name">{op.name}</span>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="payment-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="payment-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onCancel}
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-pay"
              disabled={isLoading || !selectedOperator || !validatePhone(phoneNumber)}
              data-testid="submit-payment"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Traitement...
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Payer {displayPrice.toLocaleString()} {currency}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Render processing step
  if (step === 'processing') {
    return (
      <div className="serdipay-payment processing" data-testid="payment-processing">
        <div className="processing-icon">
          <Phone size={48} className="animate-pulse" />
        </div>
        <h2>Paiement en cours...</h2>
        <p>
          Veuillez confirmer le paiement de <strong>{displayPrice.toLocaleString()} {currency}</strong> 
          sur votre téléphone {OPERATOR_STYLES[selectedOperator]?.name}.
        </p>
        
        <div className="processing-steps">
          <div className="step active">
            <CheckCircle size={20} />
            <span>Demande envoyée</span>
          </div>
          <div className={`step ${pollingCount > 0 ? 'active' : ''}`}>
            <Loader2 size={20} className="animate-spin" />
            <span>En attente de confirmation...</span>
          </div>
          <div className="step">
            <CheckCircle size={20} />
            <span>Paiement validé</span>
          </div>
        </div>

        <div className="processing-info">
          <AlertCircle size={16} />
          <span>
            Vous allez recevoir une notification sur votre téléphone. 
            Entrez votre code PIN pour confirmer.
          </span>
        </div>

        {/* Reassuring hint after ~1 min: PIN prompt may have expired */}
        {pollingCount >= 12 && pollingCount < 36 && (
          <div className="processing-timeout-hint" data-testid="payment-hint-pin" style={{background:'#FEF3C7', border:'1px solid #F59E0B', color:'#92400E', padding:'10px 14px', borderRadius:'8px', margin:'12px 0', display:'flex', gap:'8px', alignItems:'flex-start'}}>
            <AlertCircle size={16} style={{flexShrink:0, marginTop:'2px'}} />
            <span>
              Si vous n'avez pas encore reçu la demande PIN sur votre téléphone, elle a pu expirer (validité 1-2 min).
              <strong> Annulez et réessayez.</strong> Si vous avez déjà saisi votre PIN, patientez, la validation SerdiPay peut prendre jusqu'à 3 minutes.
            </span>
          </div>
        )}

        {/* After ~3 min: gentle reassurance, keep polling */}
        {pollingCount >= 36 && pollingCount < 72 && (
          <div className="processing-timeout-hint" data-testid="payment-hint-waiting" style={{background:'#DBEAFE', border:'1px solid #3B82F6', color:'#1E40AF', padding:'10px 14px', borderRadius:'8px', margin:'12px 0', display:'flex', gap:'8px', alignItems:'flex-start'}}>
            <Loader2 size={16} className="animate-spin" style={{flexShrink:0, marginTop:'2px'}} />
            <span>
              SerdiPay finalise votre paiement. Nous continuons de vérifier automatiquement.
              <strong> Vous pouvez fermer cette page :</strong> dès validation, vous recevrez un SMS et un WhatsApp de confirmation avec votre rendez-vous.
            </span>
          </div>
        )}

        {/* After ~6 min: escalation — contact support */}
        {pollingCount >= 72 && (
          <div className="processing-timeout-hint" data-testid="payment-hint-escalate" style={{background:'#FEE2E2', border:'1px solid #EF4444', color:'#991B1B', padding:'10px 14px', borderRadius:'8px', margin:'12px 0', display:'flex', gap:'8px', alignItems:'flex-start'}}>
            <AlertCircle size={16} style={{flexShrink:0, marginTop:'2px'}} />
            <span>
              La validation SerdiPay est anormalement longue.
              Si votre téléphone a bien été débité, <strong>contactez-nous au +243 840 061 625</strong> avec la référence de votre paiement — nous confirmerons manuellement votre rendez-vous.
            </span>
          </div>
        )}

        <button 
          className="btn-cancel-processing"
          onClick={() => setStep('form')}
        >
          Annuler et revenir
        </button>
      </div>
    );
  }

  // Render MANUAL fallback step — shown when the automatic gateway (SerdiPay) is unavailable
  if (step === 'manual') {
    const amount = manualInfo?.amount ?? displayPrice;
    const cur = manualInfo?.currency ?? currency;
    const ref = manualInfo?.reference ?? (transactionId ? String(transactionId).slice(-8) : '');
    const opName = manualInfo?.operator ?? OPERATOR_STYLES[selectedOperator]?.name ?? 'Mobile Money';
    return (
      <div className="serdipay-payment" data-testid="payment-manual" style={{ maxWidth: 560 }}>
        <div className="payment-header">
          <AlertCircle size={32} style={{ color: '#B45309' }} />
          <h2>Paiement Mobile Money — mode manuel</h2>
          <p style={{ color: '#78350F' }}>
            Notre passerelle automatique est momentanément indisponible. Suivez ces étapes simples pour finaliser votre rendez-vous.
          </p>
        </div>

        <div style={{
          background: '#FEF3C7', border: '1.5px solid #F59E0B', borderRadius: 12,
          padding: 18, margin: '16px 0', color: '#78350F', lineHeight: 1.6
        }}>
          <div style={{ fontWeight: 700, marginBottom: 10, fontSize: '1.05rem' }}>
            Étapes à suivre
          </div>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Ouvrez votre application <strong>{opName}</strong> ou composez le code USSD de votre opérateur.</li>
            <li>Envoyez <strong>{Number(amount).toLocaleString()} {cur}</strong> au numéro Mobile Money du CMT&nbsp;: <strong>+243 840 061 625</strong>.</li>
            <li>Dans le message ou le motif, indiquez la référence&nbsp;: <strong style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{ref}</strong>.</li>
            <li>Après le paiement, envoyez la preuve par WhatsApp au <strong>+243 840 061 625</strong>.</li>
          </ol>
        </div>

        <div style={{
          background: '#DBEAFE', border: '1px solid #60A5FA', borderRadius: 10,
          padding: 12, margin: '0 0 16px', color: '#1E3A8A', fontSize: '0.9rem'
        }}>
          Votre rendez-vous est <strong>enregistré</strong> et sera confirmé dès réception du paiement (généralement sous 15&nbsp;min en heures ouvrables).
        </div>

        <div className="payment-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            data-testid="manual-payment-close"
          >
            J'ai compris, fermer
          </button>
          <a
            href={`https://wa.me/243840061625?text=${encodeURIComponent(
              `Bonjour CMT, je viens de payer ${Number(amount).toLocaleString()} ${cur} par ${opName} pour la référence ${ref}. Voici la preuve.`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pay"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            data-testid="manual-payment-whatsapp"
          >
            <Phone size={18} />
            Envoyer preuve par WhatsApp
          </a>
        </div>
      </div>
    );
  }

  // Render success step
  if (step === 'success') {
    return (
      <div className="serdipay-payment success" data-testid="payment-success">
        <div className="success-icon">
          <CheckCircle size={64} />
        </div>
        <h2>Paiement Réussi !</h2>
        <p>
          Votre paiement de <strong>{displayPrice.toLocaleString()} {currency}</strong> a été confirmé.
        </p>
        <p className="transaction-ref">
          Référence: {transactionId}
        </p>
        <p className="success-message">
          Votre rendez-vous est maintenant confirmé.
        </p>
      </div>
    );
  }

  // Render error step
  if (step === 'error') {
    return (
      <div className="serdipay-payment error" data-testid="payment-error">
        <div className="error-icon">
          <XCircle size={64} />
        </div>
        <h2>Paiement Échoué</h2>
        <p>{error || 'Le paiement n\'a pas pu être traité.'}</p>
        <div className="error-actions">
          <button onClick={() => setStep('form')} className="btn-retry">
            Réessayer
          </button>
          <button onClick={onCancel} className="btn-cancel">
            Annuler
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export default SerdiPayPayment;
